import { htmlToText, sanitizeHtml, textToHtml, escapeHtml, measureContentHeight, fitPrefixCount, fitGuess } from './textUtils';

/**
 * Linked stories (Publisher: the story is one text; every linked box is a window on it).
 *
 * The story lives in the head box (`html` / `text`). What flows out of each box is recorded in `state.textFlow` —
 * as plain text under the box id (what every consumer reads) and, for a formatted story, as HTML under
 * `flowHtmlKey(id)` — so a continuation box shows, measures and edits its share *with* its formatting (bold words,
 * fonts, sizes, lists) instead of a plain copy.
 *
 * Editing shows each box its own *window* on the story: the head's editable holds only the part that fits in it
 * (`splitHtmlAt` keeps the formatting of both halves, `joinStoryHtml` puts them back together after every edit); a
 * continuation box holds the part flowing into it minus what flows on (`editWindowFor` locates that window in the
 * head's HTML, `joinStoryWindow` writes the edit back between the untouched parts). Plain stories take the same route
 * with plain text (`storyPrefix` / `storyTailPatch`). The flow itself is frozen while a box is being edited and
 * re-measured when editing ends, so no words show up in two boxes at once.
 *
 * Measuring: `splitHtmlToFit` finds the overflow of a formatted text by measuring its HTML (the plain-text splitter in
 * textUtils assumes the box's base font, which is wrong as soon as a headline or a larger span is in the box — the
 * words at the bottom then went missing or landed in the next box).
 */

const normalizePlain = (s) => String(s || '').replace(/\n{3,}/g, '\n\n').replace(/\n$/, '');

/** The whole story as plain text (the same text the flow engine splits). */
export const storyPlainText = (props) => (props?.html ? htmlToText(sanitizeHtml(props.html)) : props?.text || '');

/**
 * The part of the story that stays before `rest` (the text flowing into the edited box). `rest` normally is a suffix
 * of the story with its leading whitespace stripped. When it is not an exact suffix (a measurement that normalised
 * whitespace or case, a stale flow after a reformat) the suffix holding the same non-space characters is used, then
 * the last occurrence, then the suffix with as many non-space characters — never the whole story: a head that showed the
 * whole story again would repeat the words of the next box (the tester's "text from the first box in the second box").
 */
export const storyPrefix = (story, rest) => {
  if (!rest || !rest.trim()) return story; // nothing (or only blank lines) flows on: the box shows the whole text
  if (story.endsWith(rest)) return story.slice(0, story.length - rest.length);
  const n = (rest.match(/\S/g) || []).length;
  let cut = -1;
  if (n) {
    let i = story.length, seen = 0;
    while (i > 0 && seen < n) { i -= 1; if (!/\s/.test(story[i])) seen += 1; }
    if (seen === n) {
      cut = i;
      const norm = (s) => s.replace(/\s+/g, '').toLowerCase();
      if (norm(story.slice(i)) === norm(rest)) return story.slice(0, i);
    }
  }
  const at = story.lastIndexOf(rest);
  if (at >= 0) return story.slice(0, at);
  return cut >= 0 ? story.slice(0, cut) : story;
};

// the elements whose closing tag htmlToText turns into a newline (sanitizeHtml allows no headings)
const BLOCK = /^(DIV|P|LI)$/;
// the block elements a split can cut through / a join merges back (lists included)
const MERGE = /^(DIV|P|LI|UL|OL)$/;

/**
 * Where plain-text offset `P` (as counted by htmlToText) falls inside `container`: { node, offset } for a DOM range,
 * or null when it cannot be located. A `P` at the very end maps to the end of the container.
 */
export const locateSplit = (container, P) => {
  let out = ''; // what htmlToText would have produced so far
  let split = null;
  const pushNl = () => { if (!out.endsWith('\n\n')) out += '\n'; };
  const walk = (node) => {
    for (const child of Array.from(node.childNodes)) {
      if (split) return;
      if (child.nodeType === Node.TEXT_NODE) {
        const len = child.nodeValue.length;
        if (P >= out.length && P <= out.length + len) { split = { node: child, offset: P - out.length }; return; }
        out += child.nodeValue;
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        if (child.tagName === 'BR') {
          if (P === out.length) { split = { node: child.parentNode, offset: Array.prototype.indexOf.call(child.parentNode.childNodes, child) }; return; }
          pushNl();
          continue;
        }
        walk(child);
        if (split) return;
        if (BLOCK.test(child.tagName)) pushNl();
      }
    }
  };
  walk(container);
  if (!split && P >= normalizePlain(out).length) split = { node: container, offset: container.childNodes.length };
  return split;
};

/** A block that still shows something: text, a line break (a blank spacer paragraph is `<div><br></div>`) or a picture. */
const holdsContent = (el) => !!el && (!!el.textContent || !!el.querySelector('br, img'));

/**
 * Remove `block` — the paragraph a split emptied (its content went to the other half) — and the ancestors that are
 * left without content (an <li>'s <ul>). Only that block goes: the story's own blank paragraphs next to the split
 * (Publisher's small "spacer" lines between paragraphs, e.g. an 8 px blank line before a heading) are part of the text
 * and must survive every split / join of a linked story, otherwise each edit of a box would pull the paragraphs
 * closer together where the box boundary falls.
 */
const removeEmptiedBlock = (container, block) => {
  let el = block;
  while (el && el !== container && MERGE.test(el.tagName) && !holdsContent(el)) { const parent = el.parentNode; el.remove(); el = parent; }
  // nested lists: an <ul>/<ol> left without items
  Array.from(container.querySelectorAll('ul, ol')).forEach((l) => { if (!l.querySelector('li')) l.remove(); });
};

/** The split block as it starts the tail half: the first <li> of a leading list, the first block otherwise. */
const tailSplitBlock = (tail) => {
  const first = tail.firstElementChild;
  if (!first || first !== tail.firstChild) return null;
  return /^(UL|OL)$/.test(first.tagName) ? first.querySelector('li') : first;
};

/**
 * Split `html` at plain-text offset `P` (as counted by htmlToText), drop everything after it and append `rest` as
 * plain text (first line continues the paragraph at the split point, further lines become paragraphs). Returns the
 * new HTML or null when the offset could not be located. With an empty `rest` the result is the visible part of a
 * head box (emptied trailing paragraphs are dropped rather than left as blank lines).
 */
export const spliceHtmlTail = (html, P, rest) => {
  if (typeof document === 'undefined') return null;
  const container = document.createElement('div');
  container.innerHTML = sanitizeHtml(html);
  const split = locateSplit(container, P);
  if (!split) return null;
  let splitBlock = split.node.nodeType === Node.TEXT_NODE ? split.node.parentNode : split.node;
  while (splitBlock && splitBlock !== container && !BLOCK.test(splitBlock.tagName)) splitBlock = splitBlock.parentNode;
  const range = document.createRange();
  range.setStart(split.node, split.offset);
  range.setEnd(container, container.childNodes.length);
  range.deleteContents();
  if (!rest) {
    // the visible part of a head box: no placeholder paragraph where the story continues in the next box — but the
    // story's own blank paragraphs before the split stay (they are measured and shown like every other line)
    if (splitBlock && splitBlock !== container) removeEmptiedBlock(container, splitBlock);
    container.querySelectorAll('div:empty, p:empty, li:empty').forEach((b) => { b.innerHTML = '<br>'; });
    return container.innerHTML;
  }
  // Insert the rest: first line at the split point (after deleteContents the range itself has collapsed to after the
  // split block, so place the node explicitly), the others as new paragraphs after the split block.
  const lines = String(rest || '').split('\n');
  const first = document.createTextNode(lines[0]);
  if (split.node.nodeType === Node.TEXT_NODE) split.node.after(first); else split.node.appendChild(first);
  let block = first.parentNode;
  while (block && block !== container && !BLOCK.test(block.tagName)) block = block.parentNode;
  const tail = lines.slice(1);
  if (tail.length) {
    if (!block || block === container) {
      // bare text at the root: line breaks
      tail.forEach((l) => { container.appendChild(document.createElement('br')); container.appendChild(document.createTextNode(l)); });
    } else {
      let after = block;
      tail.forEach((l) => {
        const div = document.createElement('div');
        div.innerHTML = l ? escapeHtml(l) : '<br>';
        after.after(div);
        after = div;
      });
    }
  }
  // an emptied last block would collapse to nothing: keep it visible like the editor does
  container.querySelectorAll('div:empty, p:empty, li:empty').forEach((b) => { b.innerHTML = '<br>'; });
  return container.innerHTML;
};

/**
 * Property patch for the head box after the rest of the story was replaced by `restNew`. Keeps rich text when the
 * split can be reproduced exactly (validated against htmlToText), falls back to plain text otherwise.
 */
export const storyTailPatch = (headProps, prefix, restNew) => {
  const expected = normalizePlain(prefix + restNew);
  if (headProps?.html) {
    const html = spliceHtmlTail(headProps.html, prefix.length, restNew);
    if (html !== null && normalizePlain(htmlToText(html)) === expected) return { html, text: expected };
    return { html: textToHtml(expected), text: expected };
  }
  return { text: expected, html: undefined };
};

/* ------------------------------------------------------------------------------------------------------------------
 * Formatted flow: the overflow of a box carried as HTML beside the plain overflow
 * ---------------------------------------------------------------------------------------------------------------- */

/**
 * `state.textFlow[id]` is the plain text that flows out of box `id` (every consumer — Story Map, Autoflow, exports —
 * reads that). Under `flowHtmlKey(id)` the same map carries that overflow *formatted*, so a continuation box shows
 * (and measures, and edits) the story with its bold words, fonts and sizes rather than a plain copy — Publisher keeps
 * the formatting in every box of a story. A box's ids never contain "html:" (uuids), so the two never collide.
 */
export const flowHtmlKey = (id) => `html:${id}`;
export const flowHtmlOf = (textFlow, id) => (textFlow && id ? textFlow[flowHtmlKey(id)] || '' : '');

const samePlain = (a, b) => String(a || '').replace(/\s+/g, ' ').trim() === String(b || '').replace(/\s+/g, ' ').trim();

/** `html` when it is the formatted form of `plain` (same words), '' otherwise — a stale or missing formatted flow falls back to plain. */
export const richFlowFor = (html, plain) => (html && plain && samePlain(htmlToText(html), plain) ? html : '');

/**
 * Split formatted `html` (whose plain text is `plain`) where `overflow` — the suffix of `plain` that does not fit —
 * begins: { fitHtml, overflowHtml }, both complete fragments that keep their inline formatting (a paragraph split in
 * the middle appears, partially, in both). null when the split point cannot be located in the HTML.
 */
export const splitStoryHtml = (html, plain, overflow, opts) => {
  if (!overflow || !overflow.trim()) return { fitHtml: html, overflowHtml: '' };
  const prefix = storyPrefix(plain, overflow);
  if (prefix === plain) return { fitHtml: html, overflowHtml: '' };
  const parts = splitHtmlAt(html, prefix.length, opts);
  return parts ? { fitHtml: parts.prefixHtml, overflowHtml: parts.tailHtml } : null;
};

/** The option bag the split functions take for a box: its paragraph alignment (justified-last-line marker). */
export const splitOptsFor = (props) => ({ boxAlign: props?.textAlign || 'left' });

/**
 * What a linked box displays: the text flowing into it (continuation boxes) minus what flows on to the next box —
 * Publisher never shows the same words in two boxes. Heads with rich text are cut with spliceHtmlTail so the visible
 * part keeps its formatting; a continuation whose incoming text arrives formatted (flowHtmlKey) shows it formatted.
 * Returns { textOverride, htmlOverride } for ObjectRenderer.
 */
export const linkedDisplay = (obj, textFlow = {}) => {
  const p = obj?.properties || {};
  const opts = splitOptsFor(p);
  const incoming = p.linkPrevId ? textFlow[p.linkPrevId] ?? '' : undefined;
  const outgoing = p.linkNextId ? textFlow[obj.id] || '' : '';
  if (incoming !== undefined && incoming.trim()) {
    const rich = richFlowFor(flowHtmlOf(textFlow, p.linkPrevId), incoming);
    const parts = rich ? splitStoryHtml(rich, incoming, outgoing, opts) : null;
    if (parts) return { textOverride: undefined, htmlOverride: parts.fitHtml };
  }
  if (!outgoing) return { textOverride: incoming, htmlOverride: undefined };
  const story = incoming !== undefined ? incoming : storyPlainText(p);
  const prefix = storyPrefix(story, outgoing);
  if (prefix === story) return { textOverride: incoming, htmlOverride: undefined };
  if (incoming !== undefined || !p.html) return { textOverride: prefix, htmlOverride: undefined };
  // the visible part of a rich head keeps its formatting (and a paragraph that runs on keeps its last line justified)
  const cut = splitHtmlAt(p.html, prefix.length, opts);
  return cut ? { textOverride: undefined, htmlOverride: cut.prefixHtml } : { textOverride: prefix, htmlOverride: undefined };
};

/* ------------------------------------------------------------------------------------------------------------------
 * Window editing of a linked head box
 * ---------------------------------------------------------------------------------------------------------------- */

/*
 * Paragraph-fragment markers (Publisher: a paragraph that runs on from one box into the next is still one paragraph):
 * the half that *continues* in the next box gets no first-line indent (`text-indent: 0px` on its first block) and the
 * half that *ends* at the bottom of a box keeps its last line justified when the paragraph is justified
 * (`text-align-last: justify` on that block) — otherwise the fragment in box 2 reads as a new paragraph and the line at
 * the bottom of box 1 goes ragged. The markers travel with the split HTML (display, measurement, editable, export); they
 * never persist in the story: joinStoryHtml strips them wherever the halves are put back together.
 */
const INDENT_MARKER = 'text-indent';
const JUSTIFY_MARKER = 'text-align-last';
export const stripFlowMarkers = (root) => {
  root.querySelectorAll('[style]').forEach((el) => {
    if (el.style.textAlignLast) el.style.removeProperty(JUSTIFY_MARKER);
    if (el.style.textIndent && parseFloat(el.style.textIndent) === 0) el.style.removeProperty(INDENT_MARKER);
    if (!el.getAttribute('style')) el.removeAttribute('style');
  });
};
/** `html` without the paragraph-fragment markers (for a fragment that becomes a box of its own — Unlink). */
export const stripFlowMarkersHtml = (html) => {
  if (!html || typeof document === 'undefined') return html || '';
  const root = document.createElement('div');
  root.innerHTML = html;
  stripFlowMarkers(root);
  return root.innerHTML;
};

/**
 * Split rich HTML at plain-text offset `P` into the HTML before and after it. Both halves are complete fragments
 * (Range.extractContents clones the partially covered paragraph on both sides), so inline formatting survives on
 * either side; `midBlock` says the split fell inside a paragraph / list item, which joinStoryHtml must merge back.
 * `boxAlign` is the box's paragraph alignment (for the justified-last-line marker, see above).
 */
export const splitHtmlAt = (html, P, { boxAlign = 'left' } = {}) => {
  if (typeof document === 'undefined') return null;
  const container = document.createElement('div');
  container.innerHTML = sanitizeHtml(html);
  const split = locateSplit(container, P);
  if (!split) return null;
  let block = split.node.nodeType === Node.TEXT_NODE ? split.node.parentNode : split.node;
  while (block && block !== container && !BLOCK.test(block.tagName)) block = block.parentNode;
  const midBlock = !!block && block !== container;
  const range = document.createRange();
  range.setStart(split.node, split.offset);
  range.setEnd(container, container.childNodes.length);
  const tail = document.createElement('div');
  tail.appendChild(range.extractContents());
  // The split block stays in the prefix (emptied or partial) and a clone of it opens the tail. Only when *both* halves
  // carry text did the split really fall inside the paragraph — a split at a paragraph boundary must keep the break.
  // (`container` is a detached element, so Node.isConnected is false for everything in it — test containment instead)
  const prefixSideHasText = midBlock && container.contains(block) && !!block.textContent;
  const tailSideHasText = midBlock && !!tail.firstElementChild && !!tail.firstElementChild.textContent;
  // Exactly one copy of the split block keeps its content: the emptied original (split at the start of a paragraph)
  // or the empty clone (split at its end) goes — never the story's own blank paragraphs on either side of it (see
  // removeEmptiedBlock). A blank spacer paragraph the split lands on survives through its clone (it carries the <br>).
  if (midBlock && !holdsContent(block)) removeEmptiedBlock(container, block);
  else {
    const clone = tailSplitBlock(tail);
    if (clone && !holdsContent(clone)) removeEmptiedBlock(tail, clone);
  }
  const first = tail.firstElementChild;
  const mid = midBlock && prefixSideHasText && tailSideHasText && !!tail.innerHTML && !!first;
  // The split falls on the whitespace before the first word of the tail. That whitespace is dropped from the window
  // (invisible at the end of the editable, and a space typed after it would turn into a visible double space) and
  // put back by joinStoryHtml (`joiner`).
  let joiner = ' ';
  if (mid) {
    const lastText = (() => { let n = block.lastChild; while (n && n.nodeType === Node.ELEMENT_NODE && n.lastChild) n = n.lastChild; return n && n.nodeType === Node.TEXT_NODE ? n : null; })();
    if (lastText) { const m = lastText.nodeValue.match(/\s+$/); if (m) { joiner = m[0]; lastText.nodeValue = lastText.nodeValue.slice(0, -m[0].length); } }
    // one paragraph in two boxes: no first-line indent where it runs on, a justified last line where it breaks off
    const firstBlock = /^(UL|OL)$/.test(first.tagName) ? first.querySelector('li') || first : first;
    firstBlock.style.textIndent = '0px';
    if ((block.style.textAlign || boxAlign) === 'justify') block.style.textAlignLast = 'justify';
  }
  container.querySelectorAll('div:empty, p:empty, li:empty').forEach((b) => { b.innerHTML = '<br>'; });
  return { prefixHtml: container.innerHTML, tailHtml: tail.innerHTML, midBlock: mid, joiner };
};

// merge the last block of `a` with the first block of `b` (recursively for lists), both halves of one original block;
// `joiner` is the whitespace the split removed between them (see splitHtmlAt)
const mergeBoundary = (a, b, joiner = ' ') => {
  const la = a.lastElementChild, fb = b.firstElementChild;
  if (!la || !fb || la !== a.lastChild || fb !== b.firstChild || la.tagName !== fb.tagName || !MERGE.test(la.tagName)) return;
  if (/^(UL|OL)$/.test(la.tagName)) mergeBoundary(la, fb, joiner);
  else {
    // the editor's placeholder <br> of an emptied paragraph, and a trailing nbsp the editable made of a final space
    if (la.childNodes.length === 1 && la.firstChild.nodeName === 'BR') la.removeChild(la.firstChild);
    const lastText = (() => { let n = la.lastChild; while (n && n.nodeType === Node.ELEMENT_NODE && n.lastChild) n = n.lastChild; return n && n.nodeType === Node.TEXT_NODE ? n : null; })();
    if (lastText && /\u00a0$/.test(lastText.nodeValue)) lastText.nodeValue = lastText.nodeValue.replace(/\u00a0+$/, ' ');
    // the words on either side of the boundary stay two words
    const endsWithSpace = !la.textContent || /\s$/.test(la.textContent);
    const startsWithSpace = !fb.textContent || /^\s/.test(fb.textContent);
    if (!endsWithSpace && !startsWithSpace) la.appendChild(document.createTextNode(joiner || ' '));
  }
  while (fb.firstChild) la.appendChild(fb.firstChild);
  fb.remove();
};

/**
 * Append a story fragment to a *live* editable (the words that fit back into a linked box while it is edited — see
 * storyFlow.liveFlowOf `pull`): the fragment's blocks go after the editable's content; with `midBlock` its first block
 * continues the editable's last paragraph (merged like joinStoryHtml does, `joiner` = the whitespace between them). The
 * caret is left alone — nothing before it changes. `justifyLast`: the editable's last paragraph still breaks off at the
 * bottom of the box (fragment marker, see above) — otherwise that marker is cleared.
 */
export const appendStoryFragment = (el, fragmentHtml, midBlock, joiner = ' ', { justifyLast = false } = {}) => {
  if (!el || typeof document === 'undefined') return false;
  const b = document.createElement('div');
  b.innerHTML = sanitizeHtml(fragmentHtml || '');
  if (!b.firstChild) return false;
  // the editable's own helper nodes (wrap exclusions) sit at its start and never take part in the merge; blank text
  // nodes the browser left after the last paragraph would keep the merge from finding it
  while (el.lastChild && el.lastChild.nodeType === Node.TEXT_NODE && !el.lastChild.nodeValue.replace(/[\s\u200B]/g, '')) el.removeChild(el.lastChild);
  if (midBlock) mergeBoundary(el, b, joiner);
  while (b.firstChild) el.appendChild(b.firstChild);
  let last = el.lastElementChild;
  if (last && /^(UL|OL)$/.test(last.tagName)) last = last.lastElementChild || last;
  if (last && last.style) {
    if (justifyLast) last.style.textAlignLast = 'justify';
    else { last.style.removeProperty('text-align-last'); if (!last.getAttribute('style')) last.removeAttribute('style'); }
  }
  return true;
};

/** The story's HTML after its head window was edited: the edited prefix followed by the untouched tail. */
export const joinStoryHtml = (prefixHtml, tailHtml, midBlock, joiner = ' ') => {
  if (typeof document === 'undefined') return (prefixHtml || '') + (tailHtml || '');
  const a = document.createElement('div');
  a.innerHTML = sanitizeHtml(prefixHtml || '');
  const b = document.createElement('div');
  b.innerHTML = tailHtml || '';
  if (midBlock) mergeBoundary(a, b, joiner);
  while (b.firstChild) a.appendChild(b.firstChild);
  stripFlowMarkers(a); // the fragment markers of the split halves are display-only — never part of the story
  return a.innerHTML;
};

/**
 * Rich-text counterpart of textUtils.splitTextToFit: the plain text that does not fit in width × height when the
 * formatted `html` is laid out with `styleObj` (the box's base style; inline spans keep their own fonts and sizes).
 * The split is found by measuring the formatted prefix (spliceHtmlTail), word by word, with a binary search.
 */
export const splitHtmlToFit = (styleObj, html, width, height, tabs = null) => {
  const clean = sanitizeHtml(html || '');
  const plain = htmlToText(clean);
  if (!plain.trim()) return { fitText: plain, overflowText: '' };
  const words = plain.split(/(\s+)/);
  const prefixLen = (k) => words.slice(0, k).join('').length;
  const fits = (k) => {
    if (k === 0) return true;
    if (k >= words.length) return measureContentHeight(styleObj, clean, width, tabs) <= height + 0.5;
    const cut = spliceHtmlTail(clean, prefixLen(k), '');
    return cut !== null && measureContentHeight(styleObj, cut, width, tabs) <= height + 0.5;
  };
  // galloping from the number of words the box is expected to hold: a long story is never laid out whole (textUtils.fitPrefixCount)
  const lo = fitPrefixCount(words.length, fits, fitGuess(styleObj, width, height));
  if (lo >= words.length) return { fitText: plain, overflowText: '' };
  return { fitText: words.slice(0, lo).join(''), overflowText: words.slice(lo).join('').replace(/^\s+/, '') };
};

/**
 * What a linked box's editable should hold while it is edited, and how its edits are written back — computed once
 * when editing starts. Returns null for an unlinked box (normal editing).
 *  - head:  { kind: 'head', prefixHtml, tailHtml, midBlock, joiner }  editable = prefixHtml; story = join(edited, tail)
 *  - other: { kind: 'cont', headId, prefix, windowText, tailPlain, joiner, rich? }
 *           plain: editable = windowText; story = prefix + edited + joiner + tailPlain (storyTailPatch)
 *           rich (head holds HTML and the window can be located in it): editable = rich.windowHtml, the formatted
 *           share of this box; story = joinStoryWindow(rich, edited) — the formatting before, inside and after the
 *           window all survive the edit.
 */
export const editWindowFor = (box, head, textFlow = {}) => {
  const p = box?.properties || {};
  if (!p.linkPrevId && !p.linkNextId) return null;
  const opts = splitOptsFor(p);
  const outgoing = p.linkNextId ? textFlow[box.id] || '' : '';
  if (!p.linkPrevId) {
    // the head: its window is the story minus what flows on
    const html = p.html || textToHtml(p.text || '');
    if (!outgoing) return { kind: 'head', prefixHtml: html, tailHtml: '', midBlock: false };
    const story = htmlToText(sanitizeHtml(html));
    const prefix = storyPrefix(story, outgoing);
    if (prefix === story) return { kind: 'head', prefixHtml: html, tailHtml: '', midBlock: false };
    const parts = splitHtmlAt(html, prefix.length, opts);
    if (!parts) return { kind: 'head', prefixHtml: html, tailHtml: '', midBlock: false };
    return { kind: 'head', ...parts };
  }
  if (!head || head.id === box.id) return null;
  const hp = head.properties || {};
  const story = storyPlainText(hp);
  const incoming = textFlow[p.linkPrevId] ?? '';
  const prefix = storyPrefix(story, incoming);
  const windowFull = outgoing ? storyPrefix(incoming, outgoing) : incoming; // this box's share, with the whitespace before what flows on
  const hasTail = !!outgoing && windowFull !== incoming;
  const win = { kind: 'cont', headId: head.id, prefix, windowText: hasTail ? windowFull.replace(/\s+$/, '') : windowFull, tailPlain: hasTail ? outgoing : '', joiner: hasTail ? (windowFull.match(/\s+$/) || [' '])[0] : '' };
  if (hp.html && incoming.trim()) {
    // locate the window in the story's HTML: split before it, then — inside what flows in — before what flows on
    const html = sanitizeHtml(hp.html);
    const first = splitHtmlAt(html, prefix.length, opts);
    if (first && samePlain(htmlToText(first.tailHtml), incoming)) {
      let second = { prefixHtml: first.tailHtml, tailHtml: '', midBlock: false, joiner: ' ' };
      if (hasTail) {
        const inPlain = htmlToText(first.tailHtml);
        second = splitHtmlAt(first.tailHtml, storyPrefix(inPlain, outgoing).length, opts);
        if (second && !samePlain(htmlToText(second.tailHtml), outgoing)) second = null;
      }
      if (second) win.rich = { prefixHtml: first.prefixHtml, windowHtml: second.prefixHtml, tailHtml: second.tailHtml, mid1: first.midBlock, joiner1: first.joiner, mid2: second.midBlock, joiner2: second.joiner };
    }
  }
  return win;
};

/**
 * The story's HTML after a continuation window (editWindowFor(...).rich) was edited: the untouched part before it, the
 * edited window, the untouched part after it — paragraphs the window boundaries cut through are merged back. An
 * emptied window leaves prefix and tail, joined only when both boundaries fell inside one paragraph.
 */
export const joinStoryWindow = (rich, editedHtml) => {
  const edited = sanitizeHtml(editedHtml || '');
  if (!htmlToText(edited).trim()) return joinStoryHtml(rich.prefixHtml, rich.tailHtml, rich.mid1 && rich.mid2, rich.joiner1);
  const inner = joinStoryHtml(edited, rich.tailHtml, rich.mid2, rich.joiner2);
  return joinStoryHtml(rich.prefixHtml, inner, rich.mid1, rich.joiner1);
};

/* ------------------------------------------------------------------------------------------------------------------
 * Caret <-> plain-text offset inside an editable (for the live flow between linked boxes)
 * ---------------------------------------------------------------------------------------------------------------- */

const ZW = /\u200B/g;

/**
 * The plain-text offset (as locateSplit / htmlToText count it) of the selection start inside the editable `el`, or
 * null when the selection is not in it. Helper nodes of the editable (wrap exclusions) and typing placeholders
 * (zero-width spaces) do not count. placeCaretAtPlainOffset is its inverse.
 */
export const caretPlainOffset = (el) => {
  if (typeof window === 'undefined' || !el) return null;
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount) return null;
  const r = sel.getRangeAt(0);
  if (!el.contains(r.startContainer)) return null;
  const target = { node: r.startContainer, offset: r.startOffset };
  let out = '';
  let found = null;
  const pushNl = () => { if (!out.endsWith('\n\n')) out += '\n'; };
  const walk = (node) => {
    const kids = Array.from(node.childNodes);
    for (let i = 0; i < kids.length; i++) {
      if (found !== null) return;
      if (node === target.node && target.offset === i) { found = out.length; return; } // caret right before child i
      const child = kids[i];
      if (child.nodeType === Node.TEXT_NODE) {
        const txt = child.nodeValue.replace(ZW, '');
        if (child === target.node) { found = out.length + child.nodeValue.slice(0, target.offset).replace(ZW, '').length; return; }
        out += txt;
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        if (child.hasAttribute && child.hasAttribute('data-wp-exclusion')) continue;
        if (child.tagName === 'BR') { pushNl(); continue; }
        walk(child);
        if (found !== null) return;
        if (BLOCK.test(child.tagName)) pushNl();
      }
    }
    if (found === null && node === target.node && target.offset >= kids.length) found = out.length; // caret after the last child
  };
  walk(el);
  return found === null ? out.replace(/\n$/, '').length : found;
};

/** Put the caret at plain-text offset `offset` of `el` (Infinity / beyond the end: at the end). Returns true when placed. */
export const placeCaretAtPlainOffset = (el, offset) => {
  if (typeof window === 'undefined' || !el) return false;
  const sel = window.getSelection();
  if (!sel) return false;
  const range = document.createRange();
  const split = Number.isFinite(offset) && offset >= 0 ? locateSplit(el, offset) : null;
  if (split) range.setStart(split.node, split.offset); else { range.selectNodeContents(el); range.collapse(false); }
  range.collapse(true);
  sel.removeAllRanges();
  sel.addRange(range);
  return true;
};
